import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _pt from "prop-types";
var _excluded = ["data-test", "data-error", "className", "name", "type", "disabled", "placeholder", "label", "labelHidden", "optional", "hint", "hintLink", "hintComponent", "leftIcon", "rightIcon", "autoComplete", "value", "onChange", "onBlur", "onFocus", "error", "maxWidth", "wrapperWidth", "marginBottom", "sanitisePattern", "buttonOptions", "maxCharLength"];
import React, { useEffect, useRef, useState } from 'react';
import { debounce } from '@sainsburys-tech/boltui-utils';
import * as Styled from './Input.styles';
// The Input component uses a styled component wrapper, which means that we can provide an `as`
// prop to render the Input as anything we want. E.g. as='textarea'
// We have followed this guide to type it
// https://www.benmvp.com/blog/forwarding-refs-polymorphic-react-component-typescript/
var Input = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _leftIcon$component, _leftIcon$component$p, _rightIcon$component, _rightIcon$component$;

  var _ref$dataTest = _ref['data-test'],
      dataTest = _ref$dataTest === void 0 ? 'input' : _ref$dataTest,
      dataError = _ref['data-error'],
      className = _ref.className,
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? '' : _ref$name,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'text' : _ref$type,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$placeholder = _ref.placeholder,
      placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
      label = _ref.label,
      labelHidden = _ref.labelHidden,
      optional = _ref.optional,
      hint = _ref.hint,
      hintLink = _ref.hintLink,
      hintComponent = _ref.hintComponent,
      leftIcon = _ref.leftIcon,
      rightIcon = _ref.rightIcon,
      _ref$autoComplete = _ref.autoComplete,
      autoComplete = _ref$autoComplete === void 0 ? 'on' : _ref$autoComplete,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? '' : _ref$value,
      _onChange = _ref.onChange,
      _onBlur = _ref.onBlur,
      _onFocus = _ref.onFocus,
      _ref$error = _ref.error,
      error = _ref$error === void 0 ? '' : _ref$error,
      maxWidth = _ref.maxWidth,
      wrapperWidth = _ref.wrapperWidth,
      _ref$marginBottom = _ref.marginBottom,
      marginBottom = _ref$marginBottom === void 0 ? 25 : _ref$marginBottom,
      sanitisePattern = _ref.sanitisePattern,
      buttonOptions = _ref.buttonOptions,
      maxCharLength = _ref.maxCharLength,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      passwordShown = _useState2[0],
      changePasswordView = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isFocused = _useState4[0],
      toggleFocus = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      showMaxCharLengthError = _useState6[0],
      setShowMaxCharLengthError = _useState6[1];

  var _useState7 = useState(0),
      _useState8 = _slicedToArray(_useState7, 2),
      charCount = _useState8[0],
      setCharCount = _useState8[1];

  var _useState9 = useState(''),
      _useState10 = _slicedToArray(_useState9, 2),
      debouncedCharCountMessage = _useState10[0],
      setDebouncedCharCountMessage = _useState10[1];

  var displayError = showMaxCharLengthError ? "Character count limit exceeded, you can only use ".concat(maxCharLength, " characters here") : error;
  var localRef = useRef(null);
  var inputRef = ref || localRef;

  var sanitiseValue = function sanitiseValue(val) {
    if (sanitisePattern) {
      return val.replace(sanitisePattern, '');
    }

    if (!sanitisePattern && type === 'number') {
      return val.replace(/[^0-9.]/g, '');
    }

    return val;
  };

  var maxLengthCheck = function maxLengthCheck(val) {
    if (val.length > maxCharLength && !showMaxCharLengthError) {
      setShowMaxCharLengthError(true);
    }

    if (val.length <= maxCharLength && showMaxCharLengthError) {
      setShowMaxCharLengthError(false);
    }

    setCharCount(maxCharLength - val.length);
  };

  var debounceCharCount = function debounceCharCount() {
    return debounce(function () {
      return setDebouncedCharCountMessage("You have ".concat(Math.abs(charCount), " character").concat(Math.abs(charCount) === 1 ? '' : 's', " ").concat(charCount >= 0 ? 'remaining' : 'too many'));
    }, 1000);
  };

  useEffect(function () {
    if (maxCharLength) {
      var _inputRef$current;

      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.addEventListener('keyup', debounceCharCount());
    }

    return function () {
      if (maxCharLength) {
        var _inputRef$current2;

        (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.removeEventListener('keyup', debounceCharCount());
      }
    };
  });

  var handleTextAreaResize = function handleTextAreaResize() {
    var element = inputRef.current;
    element.style.height = 'auto';
    element.style.height = "".concat(element.scrollHeight + element.offsetHeight - element.clientHeight, "px");
  };

  useEffect(function () {
    if (maxCharLength) {
      maxLengthCheck(value);
    }

    if (props.as === 'textarea') {
      window.onresize = handleTextAreaResize;
    }
  }, [value, maxCharLength]);
  var inputType = type;

  if (type === 'password' && passwordShown) {
    inputType = 'text';
  }

  if (type === 'number') {
    inputType = 'tel'; // use tel for better cross browser compatibility whilst retaining number keyboard on mobile
  }

  var hasCustomButton = buttonOptions === null || buttonOptions === void 0 ? void 0 : buttonOptions.show;
  return /*#__PURE__*/React.createElement(Styled.Wrapper, {
    className: className,
    wrapperWidth: wrapperWidth,
    marginBottom: marginBottom
  }, label && /*#__PURE__*/React.createElement(Styled.Label, {
    className: labelHidden ? 'sr-only' : null,
    id: "".concat(dataTest, "-").concat(name, "-label"),
    htmlFor: name,
    hasSecondLine: !!(hint || hintLink || hintComponent || displayError)
  }, label, optional && /*#__PURE__*/React.createElement(Styled.OptionalLabel, null, optional)), displayError && /*#__PURE__*/React.createElement(Styled.ErrorMessage, {
    "data-test": "".concat(dataTest, "-error"),
    id: "".concat(name, "-error-message"),
    role: "alert"
  }, displayError), (hint || hintLink || hintComponent) && /*#__PURE__*/React.createElement(Styled.HintRow, {
    className: "hint-row"
  }, hint && /*#__PURE__*/React.createElement(Styled.Hint, null, hint), hintLink && /*#__PURE__*/React.createElement(Styled.Link, {
    href: hintLink.url,
    target: "_blank"
  }, hintLink.text), hintComponent), !!maxCharLength && !disabled && /*#__PURE__*/React.createElement(Styled.CharCount, {
    maxWidth: maxWidth,
    "data-test": "".concat(dataTest, "-char-count"),
    id: "".concat(name, "-char-count")
  }, "You have ", /*#__PURE__*/React.createElement("strong", null, Math.abs(charCount)), ' ', "character".concat(Math.abs(charCount) === 1 ? '' : 's', " ").concat(charCount >= 0 ? 'remaining' : 'too many'), /*#__PURE__*/React.createElement("span", {
    className: "sr-only",
    role: "status"
  }, debouncedCharCountMessage)), /*#__PURE__*/React.createElement(Styled.InputRow, {
    maxWidth: maxWidth,
    hasCustomButton: hasCustomButton
  }, /*#__PURE__*/React.createElement(Styled.Input, _extends({
    ref: inputRef,
    "data-test": dataTest,
    "data-error": dataError,
    id: name,
    name: name,
    type: inputType,
    disabled: disabled,
    placeholder: placeholder,
    leftIcon: !!leftIcon,
    leftIconWidth: leftIcon === null || leftIcon === void 0 ? void 0 : (_leftIcon$component = leftIcon.component) === null || _leftIcon$component === void 0 ? void 0 : (_leftIcon$component$p = _leftIcon$component.props) === null || _leftIcon$component$p === void 0 ? void 0 : _leftIcon$component$p.width,
    rightIcon: !!rightIcon,
    rightIconWidth: rightIcon === null || rightIcon === void 0 ? void 0 : (_rightIcon$component = rightIcon.component) === null || _rightIcon$component === void 0 ? void 0 : (_rightIcon$component$ = _rightIcon$component.props) === null || _rightIcon$component$ === void 0 ? void 0 : _rightIcon$component$.width,
    autoComplete: autoComplete,
    value: sanitiseValue(value),
    onChange: function onChange(evt) {
      // eslint-disable-next-line no-param-reassign
      evt.target.value = sanitiseValue(evt.target.value);

      if (_onChange) {
        _onChange(evt);
      }

      if (props.as === 'textarea') {
        handleTextAreaResize();
      }
    },
    onBlur: function onBlur(evt) {
      if (_onBlur) {
        _onBlur(evt);
      }

      toggleFocus(false);
    },
    onFocus: function onFocus(evt) {
      if (_onFocus) {
        _onFocus(evt);
      }

      toggleFocus(true);
    },
    error: displayError,
    "aria-invalid": !!displayError,
    "aria-describedby": displayError ? "".concat(name, "-error-message") : "".concat(dataTest, "-").concat(name, "-label"),
    hasCustomButton: hasCustomButton
  }, props)), leftIcon && !leftIcon.onClick && /*#__PURE__*/React.createElement(Styled.IconContainer, {
    position: "left",
    "data-test": "".concat(dataTest, "-leftIcon")
  }, leftIcon.component), rightIcon && !rightIcon.onClick && /*#__PURE__*/React.createElement(Styled.IconContainer, {
    position: "right",
    "data-test": "".concat(dataTest, "-rightIcon")
  }, rightIcon.component), leftIcon && leftIcon.onClick && /*#__PURE__*/React.createElement(Styled.Button, {
    type: "button",
    className: leftIcon.className,
    "data-test": "".concat(dataTest, "-leftIcon"),
    tabIndex: leftIcon.tabIndex,
    position: "left",
    onClick: leftIcon.onClick,
    "aria-label": leftIcon.ariaLabel
  }, leftIcon.component), rightIcon && rightIcon.onClick && /*#__PURE__*/React.createElement(Styled.Button, {
    type: "button",
    "data-test": "".concat(dataTest, "-rightIcon"),
    tabIndex: rightIcon.tabIndex,
    position: "right",
    onClick: rightIcon.onClick,
    "aria-label": rightIcon.ariaLabel
  }, rightIcon.component), type === 'password' && /*#__PURE__*/React.createElement(Styled.PasswordToggle, {
    type: "button",
    error: displayError,
    isInputFocused: isFocused,
    onClick: function onClick() {
      return passwordShown ? changePasswordView(false) : changePasswordView(true);
    }
  }, passwordShown ? 'Hide' : 'Show'), hasCustomButton && /*#__PURE__*/React.createElement(Styled.CustomButton, _extends({
    isInputFocused: isFocused
  }, buttonOptions), buttonOptions.text)));
});
Input.propTypes = {
  'data-test': _pt.string,
  'data-error': _pt.bool,
  label: _pt.node,
  labelHidden: _pt.bool,
  optional: _pt.string,
  hint: _pt.string,
  hintLink: _pt.shape({
    text: _pt.string.isRequired,
    url: _pt.string.isRequired
  }),
  hintComponent: _pt.node,
  leftIcon: _pt.shape({
    component: _pt.element.isRequired,
    tabIndex: _pt.number,
    ariaLabel: _pt.string,
    onClick: _pt.func,
    className: _pt.string
  }),
  leftIconWidth: _pt.string,
  rightIcon: _pt.shape({
    component: _pt.element.isRequired,
    tabIndex: _pt.number,
    ariaLabel: _pt.string,
    onClick: _pt.func
  }),
  rightIconWidth: _pt.string,
  buttonOptions: _pt.oneOfType([_pt.elementType, _pt.shape({
    show: _pt.bool.isRequired,
    text: _pt.string.isRequired
  })]),
  error: _pt.string,
  maxWidth: _pt.string,
  wrapperWidth: _pt.string,
  marginBottom: _pt.number,
  sanitisePattern: _pt.instanceOf(RegExp),
  maxCharLength: _pt.number,
  value: _pt.string
};
export default Input;